import React, { useContext, useEffect, useState } from 'react';

import { Skeleton } from '@mui/lab'

import { SortContext, getNodeValue } from '../../contexts/SortContext';

import { TableHeaderCell } from '../TableHeaderCell';
import { InfoOutlined, More, MoreHorizRounded } from '@mui/icons-material';
import { NodeTableRow } from './NodeTableRow';
import { EmptyView } from '../EmptyView';
import { ScrollBottomTrigger } from '../ScrollBottomTrigger';
import { Menu } from '@mui/material';
import moment from 'moment';

export function CheckboxTableCell({ slug, ...props }) {
  const {
    checkedNodeIds,
    setCheckedNodeIds
  } = useContext(SortContext)

  return (
    <td>
      <input type="checkbox" 
        onChange={(event) => {
          if (event.target.checked) {
            setCheckedNodeIds({
              ...checkedNodeIds,
              [slug]: true
            })
          } else {
            const newCheckedNodeIds = {...checkedNodeIds}
            delete newCheckedNodeIds[slug]
            setCheckedNodeIds(newCheckedNodeIds)
          }
        }}
        checked={ slug in checkedNodeIds}
        {...props}
      />
    </td>
  )
}

export function NodeTable({
  nodes,
  renderRowClassNames = () => ``,
  columns,
  canDelete = false,
  loading = false,
  onDeleteClicked=() => {},
  renderTableHeaderCells = () => {},
  getValueOfNode = getNodeValue,
  defaultSortByFieldId,
  defaultSortByDirection = 'asc',
  renderTableRow = () => {},
  getRealNode = ({ node }) => node,
  getContextMenuItems = (nodes) => [],
  onNodesChecked = ({ nodes }) => [],
  draggable = false,
  hasSubRows,
  expanderEnabled,
  expanded = false,
  getRowExpanded = null,
  getRowShowExpander = null,
  renderSubRows,
  onTrigger = () => {},
  getSubRows = () => null,
  emptyHeading='Empty',
  emptyIcon=InfoOutlined,
  emptyText='No nodes found',
  onSortByChanged = () => {},
  sortLocal = false,
  ...props
}) {
  const [sortBy, setSortBy] = useState(defaultSortByFieldId)
  const [sortByDirection, setSortByDirection] = useState(defaultSortByDirection)
  const [filters, setFilters] = useState({}) 
  const [checkedNodeIds, setCheckedNodeIds] = useState({})
  const [selectedNodeIds, setSelectedNodeIds] = useState({})

  //const [sortedNodes, setSortedNodes] = useState(nodes)

  useEffect(() => {
    onNodesChecked({ nodes: nodes.filter(n => n.slug in checkedNodeIds) })
  }, [checkedNodeIds])

  const sortNodes = () => {
    onSortByChanged({
      sortByFieldId: sortBy,
      sortByDirection: sortByDirection
    })
  }

  useEffect(sortNodes, [nodes, sortBy, sortByDirection])

  let sortedNodes = [...nodes]

  const compareNode = (column) => (node1, node2) => {
    if (!column) return 0
    const a = node1[column.id]
    const b = node2[column.id]
    if (column.sort instanceof Function) {
      return column.sort({ a, b, direction: sortByDirection, column })
    } else {
      try {
        const ret = a.localeCompare(b) * (sortByDirection === 'desc' ? 1 : -1)
        return ret
      } catch (e) {
        if (a instanceof Array && b instanceof Array) {
          if (a.length > 0 && b.length > 0) {
            return compareNode({ id: 'name' })(a[0], b[0])
          } else {
            return 0
          }
        } else if (!isNaN(a) && !isNaN(b)) {
          return (a - b) * (sortByDirection === 'desc' ? -1 : 1);
        } else if (column.type === 'datetime') {
          return (moment(a).isBefore(moment(b)) ? 1 : -1) * (sortByDirection === 'desc' ? -1 : 1)
        } else {
          if (a instanceof Object && b instanceof Object) {
            return (a.name.localeCompare(b.name)) * (sortByDirection === 'desc' ? -1 : 1)
          }
          return a - b * (sortByDirection === 'desc' ? -1 : 1) 
        }
      }
    }
  }

  if (sortBy?.length > 0 && sortLocal) {
    const column = columns.find((c) => c.id === sortBy)
    sortedNodes.sort(compareNode(column))
  }

  return (
    <SortContext.Provider value={{ 
      setSortBy,
      sortBy,
      sortByDirection,
      checkedNodeIds,
      selectedNodeIds,
      setSelectedNodeIds,
      getContextMenuItems,
      setCheckedNodeIds,
      getSubRows,
      getRealNode,
      renderTableRow,
      getValueOfNode,
      setSortByDirection,
      //sortedNodes,
      nodes,
      filters,
      setFilters
    }}>
      <>
        <table class="table" cellSpacing={0} tabIndex={0} {...props}>
          <thead>
            <tr>
              {hasSubRows &&
                <th></th>
              }
              <th>
                <input
                  type="checkbox"
                  onClick={(event) => {
                    if (event.target.checked) {
                      const newCheckedNodeIds = {}
                      nodes.forEach(artist => {
                        newCheckedNodeIds[artist.slug] = true
                      })
                      setCheckedNodeIds(newCheckedNodeIds)
                    } else {
                      setCheckedNodeIds({})
                    }
                  }}
                  checked={Object.values(checkedNodeIds)?.length >= nodes.length} 
                />
              </th>
              {columns.map(column => {
                return (
                  <TableHeaderCell style={{ padding: 0, ...column.headerStyle }} width={column.width} key={column.id} field={column.id} sortable={column.sortable}>{column.name}</TableHeaderCell>
                )
              })}
              <th>
                <MoreHorizRounded />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [1,2,3,4,5,6,7,8,9,10].map( n => (
                <tr key={`${n}`}>
                  <td></td>
                  <td></td>
                  {columns.map(c => (
                    <td style={{ background: sortBy === c.id ? 'rgba(127, 127, 127, .1)' : '', }} key={`${c.id}`}>
                      <Skeleton />
                    </td>
                  ))}
                  <td>
                   <Skeleton />
                  </td>
                  <td>
                    <Menu>
                    </Menu>
                  </td>
                </tr>
              ))
            ) : sortedNodes.map(
              node => {
                const selected = node.slug in selectedNodeIds
                const checked = node.slug in checkedNodeIds

                return ( 
                  <NodeTableRow
                    level={0}
                    renderRowClassNames={renderRowClassNames}
                    hasSubRows={hasSubRows}
                    rows={getSubRows(node)}
                    renderSubRows={renderSubRows}
                    node={node}
                    selected={selected}
                    checked={checked}
                    columns={columns}
                    showExpander={getRowShowExpander instanceof Function ? getRowShowExpander({ node }) : true}
                    expanded={(getRowExpanded instanceof Function && getRowExpanded({ node })) || expanded}
                    onDeleteClicked={() => {
                      onDeleteClicked(
                        nodes.filter(n => Object.keys(selectedNodeIds).find(slug => n.slug === slug))
                      )
                    }}
                  /> 
                )
              } 
            )}
          </tbody>
          {expanderEnabled && (
            <tfoot>
              <ScrollBottomTrigger
                onTrigger={onTrigger}
                enabled={expanderEnabled}
              >

              </ScrollBottomTrigger>
            </tfoot>
          )}
        </table>
        {sortedNodes?.length < 1 && <EmptyView Icon={emptyIcon} heading={emptyHeading} text={emptyText} />}
      </>
    </SortContext.Provider>
  )
}